import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ref, get, onValue } from 'firebase/database';
import { database } from '../../config/firebase';

const ChurchDetail = () => {
  const [church, setChurch] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    const fetchChurchData = async () => {
      try {
        setLoading(true);

        // Récupérer les données de l'église
        const churchRef = ref(database, `churches/${id}`);
        const churchSnapshot = await get(churchRef);

        if (!churchSnapshot.exists()) {
          throw new Error("Cette église n'existe pas");
        }

        const churchData = {
          id,
          ...churchSnapshot.val()
        };

        setChurch(churchData);

        // Écouter les statistiques de l'église en temps réel
        const statsRef = ref(database, `statistics/churches/${id}`);
        const unsubscribeStats = onValue(statsRef, (snapshot) => {
          if (snapshot.exists()) {
            setStatistics(snapshot.val());
          } else {
            // Si les statistiques n'existent pas encore, initialiser avec des zéros
            setStatistics({
              totalMembres: 0,
              hommes: 0,
              femmes: 0,
              baptises: 0
            });
          }
        }, (error) => {
          console.error("Erreur lors de la récupération des statistiques:", error);
        });

        return () => {
          unsubscribeStats();
        };
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchChurchData();
  }, [id]);

  // Formater la date de création
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date inconnue';
    const date = new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4" role="alert">
          <p>{error}</p>
          <div className="mt-4">
            <Link to="/churches" className="text-red-700 underline hover:text-red-600">
              Retour à la liste des églises
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center">
          <Link to="/churches" className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
            <svg className="-ml-1 mr-1 h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Retour à la liste
          </Link>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">{church.nom}</h1>
          <div className="mt-4 sm:mt-0">
            <Link
              to={`/churches/${id}/edit`}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              Modifier
            </Link>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Informations détaillées
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Détails et statistiques de l'église.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Nom</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{church.nom}</dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Localisation</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {church.localisation || 'Non spécifiée'}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Description</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {church.description || 'Aucune description'}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Contact</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {church.contact || 'Non spécifié'}
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Date d'ajout</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatDate(church.createdAt)}
              </dd>
            </div>
            {church.updatedAt && (
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Dernière modification</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {formatDate(church.updatedAt)}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Statistiques */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Statistiques des membres
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Aperçu des données démographiques.
          </p>
        </div>

        <div className="border-t border-gray-200">
          {statistics ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-6">
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-blue-600">{statistics.totalMembres}</div>
                <div className="text-sm font-medium text-blue-800 mt-1">Total des membres</div>
              </div>

              <div className="bg-indigo-50 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-indigo-600">{statistics.hommes}</div>
                <div className="text-sm font-medium text-indigo-800 mt-1">Hommes</div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-purple-600">{statistics.femmes}</div>
                <div className="text-sm font-medium text-purple-800 mt-1">Femmes</div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg text-center">
                <div className="text-3xl font-bold text-green-600">{statistics.baptises}</div>
                <div className="text-sm font-medium text-green-800 mt-1">Baptisés</div>
              </div>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              Chargement des statistiques...
            </div>
          )}
        </div>

        {/* Actions membres */}
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <div className="flex justify-end">
            <Link
              to={`/members?churchId=${id}`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
              Voir les membres
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChurchDetail;
